import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Users, UserPlus, Mail, Loader2, Copy, Check, Shield, Trash2, ShieldOff, ShieldCheck } from 'lucide-react';
import { api } from '@/utils/api';
import Layout from '@/components/Layout';

const ROLE_LABEL = (r, isAr) => {
  const map = isAr
    ? { owner: 'مالك', asset_manager: 'مدير أصول', pmc: 'شركة إدارة', operations: 'مدير عمليات', maintenance: 'فريق صيانة', finance: 'مدير مالي', marketing: 'تسويق', tenant: 'مستأجر', investor: 'مستثمر', enterprise: 'مدير مؤسسي', super_admin: 'مدير عام', platform_admin: 'مدير المنصة' }
    : { owner: 'Property Owner', asset_manager: 'Asset Manager', pmc: 'PMC', operations: 'Operations', maintenance: 'Maintenance', finance: 'Finance', marketing: 'Marketing', tenant: 'Tenant', investor: 'Investor (RO)', enterprise: 'Enterprise Admin', super_admin: 'Super Admin', platform_admin: 'Platform Admin' };
  return map[r] || r;
};

const ROLE_COLOR = {
  owner: 'bg-blue-100 text-blue-700', asset_manager: 'bg-purple-100 text-purple-700', pmc: 'bg-indigo-100 text-indigo-700', operations: 'bg-teal-100 text-teal-700',
  maintenance: 'bg-amber-100 text-amber-700', finance: 'bg-green-100 text-green-700', marketing: 'bg-pink-100 text-pink-700', tenant: 'bg-cyan-100 text-cyan-700',
  investor: 'bg-yellow-100 text-yellow-700', enterprise: 'bg-slate-100 text-slate-700', super_admin: 'bg-red-100 text-red-700', platform_admin: 'bg-orange-100 text-orange-700',
};

export default function Team() {
  const { i18n } = useTranslation();
  const { permissions } = useAuth();
  const isAr = i18n.language === 'ar';
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [form, setForm] = useState({ email: '', full_name: '', role: 'finance', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [copiedToken, setCopiedToken] = useState('');
  const [flash, setFlash] = useState('');

  const invitableRoles = permissions?.invitable_roles || [];

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [u, i] = await Promise.all([api.listTeamUsers(), api.listInvitations()]);
      setUsers(u.data); setInvites(i.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const submitInvite = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.email || !form.role) { setError(isAr ? 'البريد والدور مطلوبان' : 'Email and role are required'); return; }
    setSubmitting(true);
    try {
      const res = await api.createInvitation(form);
      setCopiedToken(res.data.token);
      setFlash(isAr ? 'تم إرسال الدعوة' : 'Invitation sent');
      setForm({ email: '', full_name: '', role: 'finance', message: '' });
      fetchAll();
      setTimeout(() => setFlash(''), 3000);
    } catch (e) {
      setError(e?.response?.data?.detail || (isAr ? 'فشل إرسال الدعوة' : 'Failed to send invitation'));
    } finally { setSubmitting(false); }
  };

  const revokeInvite = async (id) => {
    if (!window.confirm(isAr ? 'إلغاء الدعوة؟' : 'Revoke invitation?')) return;
    try { await api.revokeInvitation(id); fetchAll(); } catch (e) { console.error(e); }
  };

  const toggleUser = async (u) => {
    try {
      await api.updateTeamUser(u.id, { is_active: !u.is_active });
      fetchAll();
    } catch (e) { alert(e?.response?.data?.detail || 'Failed'); }
  };

  const changeRole = async (u, newRole) => {
    try {
      await api.updateTeamUser(u.id, { custom_role: newRole });
      fetchAll();
    } catch (e) { alert(e?.response?.data?.detail || 'Failed'); }
  };

  const copyInviteLink = (token) => {
    const url = `${window.location.origin}/accept-invite?token=${token}`;
    navigator.clipboard.writeText(url);
    setCopiedToken(token);
    setTimeout(() => setCopiedToken(''), 2000);
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center"><Users className="h-5 w-5 text-primary" strokeWidth={1.5} /></div>
            <div>
              <h1 className="font-heading text-2xl font-bold text-gray-900">{isAr ? 'الفريق' : 'Team'}</h1>
              <p className="text-sm text-gray-500">{isAr ? 'إدارة المستخدمين والدعوات داخل منظمتك' : 'Manage users and invitations in your organization'}</p>
            </div>
          </div>
          <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
            <DialogTrigger asChild>
              <Button className="gradient-primary text-white rounded-xl" data-testid="invite-btn"><UserPlus className="h-4 w-4 me-2" /> {isAr ? 'دعوة مستخدم' : 'Invite user'}</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{isAr ? 'دعوة عضو جديد' : 'Invite a new member'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={submitInvite} className="space-y-4">
                <div>
                  <Label className="text-xs text-gray-600 font-medium">{isAr ? 'البريد الإلكتروني' : 'Email'}*</Label>
                  <Input type="email" required value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="user@company.com" data-testid="invite-email" />
                </div>
                <div>
                  <Label className="text-xs text-gray-600 font-medium">{isAr ? 'الاسم الكامل' : 'Full name'}</Label>
                  <Input value={form.full_name} onChange={e => setForm({ ...form, full_name: e.target.value })} data-testid="invite-name" />
                </div>
                <div>
                  <Label className="text-xs text-gray-600 font-medium">{isAr ? 'الدور' : 'Role'}*</Label>
                  <Select value={form.role} onValueChange={v => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="invite-role"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {invitableRoles.map(r => <SelectItem key={r} value={r}>{ROLE_LABEL(r, isAr)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <div className="mt-1.5 text-[11px] text-gray-500">{isAr ? 'لا يمكنك دعوة مالك آخر؛ فقط الأدوار التشغيلية.' : "You can't invite another owner — only operational roles."}</div>
                </div>
                <div>
                  <Label className="text-xs text-gray-600 font-medium">{isAr ? 'رسالة (اختياري)' : 'Message (optional)'}</Label>
                  <textarea rows={2} value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} className="w-full mt-1 px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-primary resize-none" />
                </div>
                {error && <div className="text-xs text-red-600 bg-red-50 p-2 rounded-lg" data-testid="invite-error">{error}</div>}
                <Button type="submit" disabled={submitting} className="w-full gradient-primary text-white rounded-xl" data-testid="invite-submit">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Mail className="h-4 w-4 me-2" /> {isAr ? 'إرسال الدعوة' : 'Send invitation'}</>}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {flash && <div className="text-sm text-green-700 bg-green-50 border border-green-100 px-4 py-2.5 rounded-xl" data-testid="team-flash">{flash}</div>}

        {/* Members */}
        <Card className="shadow-soft border-gray-100">
          <CardHeader className="pb-2"><CardTitle className="font-heading text-base">{isAr ? 'الأعضاء' : 'Members'} ({users.length})</CardTitle></CardHeader>
          <CardContent>
            {loading ? <div className="text-center py-6 text-gray-500"><Loader2 className="h-4 w-4 animate-spin inline" /></div> : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{isAr ? 'الاسم' : 'Name'}</TableHead>
                    <TableHead>{isAr ? 'البريد' : 'Email'}</TableHead>
                    <TableHead>{isAr ? 'الدور' : 'Role'}</TableHead>
                    <TableHead>{isAr ? 'الحالة' : 'Status'}</TableHead>
                    <TableHead>{isAr ? 'إجراءات' : 'Actions'}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map(u => {
                    const role = u.custom_role || u.role;
                    return (
                      <TableRow key={u.id} data-testid={`team-row-${u.id}`}>
                        <TableCell className="font-medium">{u.full_name}</TableCell>
                        <TableCell className="text-sm text-gray-600">{u.email}</TableCell>
                        <TableCell><span className={`px-2 py-0.5 rounded text-xs font-medium ${ROLE_COLOR[role] || 'bg-gray-100 text-gray-700'}`}>{ROLE_LABEL(role, isAr)}</span></TableCell>
                        <TableCell>
                          {u.is_active === false
                            ? <span className="text-xs text-red-600 bg-red-50 px-2 py-0.5 rounded">{isAr ? 'معطل' : 'Disabled'}</span>
                            : <span className="text-xs text-green-700 bg-green-50 px-2 py-0.5 rounded">{isAr ? 'نشط' : 'Active'}</span>
                          }
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            {invitableRoles.includes(role) && (
                              <Select value={role} onValueChange={(v) => changeRole(u, v)}>
                                <SelectTrigger className="h-7 text-xs w-32" data-testid={`team-role-select-${u.id}`}><SelectValue /></SelectTrigger>
                                <SelectContent>{invitableRoles.map(r => <SelectItem key={r} value={r}>{ROLE_LABEL(r, isAr)}</SelectItem>)}</SelectContent>
                              </Select>
                            )}
                            <Button variant="ghost" size="sm" onClick={() => toggleUser(u)} title={u.is_active === false ? 'Enable' : 'Disable'} data-testid={`team-toggle-${u.id}`}>
                              {u.is_active === false ? <ShieldCheck className="h-4 w-4 text-green-600" /> : <ShieldOff className="h-4 w-4 text-red-500" />}
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {users.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-sm text-gray-400 py-6">{isAr ? 'لا يوجد أعضاء' : 'No members yet'}</TableCell></TableRow>}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Invitations */}
        <Card className="shadow-soft border-gray-100">
          <CardHeader className="pb-2"><CardTitle className="font-heading text-base">{isAr ? 'الدعوات' : 'Invitations'} ({invites.length})</CardTitle></CardHeader>
          <CardContent>
            {invites.length === 0 ? (
              <div className="text-center py-8 text-sm text-gray-400">{isAr ? 'لا توجد دعوات' : 'No invitations yet'}</div>
            ) : (
              <div className="space-y-2">
                {invites.map(inv => (
                  <div key={inv.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl" data-testid={`invite-row-${inv.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">{inv.email}</span>
                        <span className={`text-xs px-1.5 py-0.5 rounded ${ROLE_COLOR[inv.role] || 'bg-gray-100 text-gray-700'}`}>{ROLE_LABEL(inv.role, isAr)}</span>
                        <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${inv.status === 'accepted' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>{inv.status}</span>
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">{isAr ? 'دعوة بواسطة' : 'Invited by'} {inv.invited_by_name} · {new Date(inv.created_at).toLocaleDateString()}</div>
                    </div>
                    <div className="flex items-center gap-1">
                      {inv.status === 'pending' && (
                        <>
                          <Button variant="ghost" size="sm" onClick={() => copyInviteLink(inv.token)} title="Copy invite link" data-testid={`invite-copy-${inv.id}`}>
                            {copiedToken === inv.token ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4 text-gray-500" />}
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => revokeInvite(inv.id)} data-testid={`invite-revoke-${inv.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="p-4 bg-blue-50 border border-blue-100 rounded-2xl flex items-start gap-3">
          <Shield className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-blue-900 leading-relaxed">
            <div className="font-bold mb-1">{isAr ? 'كيف تعمل الأدوار في REVA' : 'How roles work in REVA'}</div>
            <p>{isAr
              ? 'الأدوار التشغيلية (مالي، عمليات، صيانة، تسويق، مستثمر) لا يمكن للمستخدمين الجدد التسجيل بها مباشرة — يجب دعوتهم منك. هذا يضمن عدم وصول أي شخص خارجي إلى بياناتك المالية أو ملفات المستأجرين.'
              : 'Operational roles (Finance, Operations, Maintenance, Marketing, Investor) cannot be self-registered — they must be invited by you. This guarantees no outside user can reach your financials or tenant data.'}
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
}
